import { browserLogger } from "@@/settings";
import { TinyColor } from "@ctrl/tinycolor";
import { useTheme } from "@emotion/react";
import type { ColorTheme, HexColor } from "@towni/common";
import { colorFactory, hexColorSetNames } from "@towni/common";
import type { ReactNode } from "react";
import ReactDOM from "react-dom";

type ColorThemeShades = {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    950: string;
    1000: string;
    base: string;
    copy: string;
    surface: string;
    "surface-10": string;
    "surface-50": string;
    "surface-90": string;
    outline: string;
};

const _colorSetCache = new Map<HexColor, string>();

const _generateColorSet = (hex500: HexColor, colorSetName: string): string => {
    const cached = _colorSetCache.get(hex500);
    if (cached) return cached;

    const color500 = new TinyColor(hex500);
    const hsl500 = color500.toHsl();

    // Extract the base HSL values
    const h = hsl500.h;
    const s = Math.round(hsl500.s * 100); // Convert to percentage

    const color100 = new TinyColor({ h, s: s * 0.697, l: 95 });
    const color900 = new TinyColor({ h, s, l: hsl500.l * 0.6 });
    // Create shades with appropriate lightness adjustments
    // The pattern follows your example where:
    // - Lower numbers are lighter shades
    // - Higher numbers are darker shades
    const shades: ColorThemeShades = {
        50: new TinyColor({ h, s: s * 0.697, l: 97.5 }).toHslString(),
        100: color100.toHslString(),
        200: new TinyColor({ h, s: s * 0.697, l: 75 }).toHslString(),
        300: new TinyColor({ h, s: s * 0.697, l: 50 }).toHslString(),
        400: new TinyColor({ h, s: s * 0.872, l: 40 }).toHslString(),
        500: color500.toHslString(), // Original color
        600: new TinyColor({ h, s, l: hsl500.l * 0.85 }).toHslString(),
        700: new TinyColor({ h, s, l: hsl500.l * 0.73 }).toHslString(),
        800: new TinyColor({ h, s, l: hsl500.l * 0.67 }).toHslString(),
        900: color900.toHslString(),
        950: new TinyColor({ h, s, l: hsl500.l * 0.42 }).toHslString(),
        1000: new TinyColor({ h, s, l: hsl500.l * 0.3 }).toHslString(),
        base: color500.toHslString(), // Same as 500
        copy: new TinyColor({ h: 0, s: 0, l: 100 }).toHslString(), // White text for contrast
        surface: color500.toHslString(), // Same as 500
        "surface-10": color100.toHslString(), // Same as 100
        "surface-50": color500.toHslString(), // Same as 500
        "surface-90": color900.toHslString(), // Same as 900
        outline: new TinyColor({ h, s: s * 0.523, l: 76 }).toHslString(), // Same as 200
    };

    // Generate CSS variables
    let cssVars = "";

    Object.entries(shades).forEach(([shade, value]) => {
        if (shade === "base") {
            cssVars += `\t--color-${colorSetName}: ${value};\n`;
        } else if (
            [
                "copy",
                "surface",
                "surface-10",
                "surface-50",
                "surface-90",
                "outline",
            ].includes(shade)
        ) {
            cssVars += `\t--color-${colorSetName}-${shade}: ${value};\n`;
        } else {
            cssVars += `\t--color-${colorSetName}-${shade}: ${value};\n`;
        }
    });

    _colorSetCache.set(hex500, cssVars);
    return cssVars;
};

const _colorThemeFactory = (colorTheme: ColorTheme): string => {
    let result = "";
    for (const [key, themeSet] of Object.entries(colorTheme)) {
        if (themeSet === "COLOR_THEME") continue;
        if (themeSet._type === "COLOR") continue;

        const name = key as keyof ColorTheme;
        // ignore all that's not part of hex color set
        if (!hexColorSetNames.some(setName => setName === name)) continue;
        if (!themeSet) {
            browserLogger.warn("No main color found for theme set:", {
                name,
                colorTheme,
            });
            continue;
        }
        const mainColor = colorFactory(themeSet.main.value.asHex);
        result += _generateColorSet(mainColor.value.asHex, name);
    }
    return result;
};

/**
 * Helper function to convert CSS variable string to a style object
 */
function cssVariablesStringToObject(cssVars: string): Record<string, string> {
    const styleObj: Record<string, string> = {};
    const lines = cssVars.split("\n");

    for (const line of lines) {
        const match = line.match(/\t--([^:]+):\s*(.+);/);
        if (match) {
            const [, name, value] = match;
            styleObj[`--${name}`] = value.trim();
        }
    }

    return styleObj;
}

/**
 * Turns an old color theme into a new tailwind theme, using the main color
 * and calculating the rest of the shades.. to match our new tailwind themes.
 *
 * There might be some differences in the shades, but the main color should be the same.
 *
 * Sets the Tailwind CSS theme for its child components by applying a theme-specific class name.
 *
 * @param props - The component props.
 * @param props.colorTheme - The color theme to apply, used to generate the appropriate Tailwind class name.
 * @param props.children - Optional React children to be rendered within the themed context.
 *
 * @remarks
 * The component uses a `<div>` with `display: contents` to avoid introducing unnecessary DOM elements,
 * while still applying the theme class to its children.
 */
const TailwindThemeProvider = (props: {
    readonly colorTheme: ColorTheme;
    readonly children?: ReactNode;
}) => {
    const { colorTheme } = props;
    const className = _colorThemeFactory(colorTheme);
    const themeStyle = {
        ...cssVariablesStringToObject(className),
        display: "contents",
    };

    return (
        <div className={className} style={themeStyle}>
            {props.children}
        </div>
    );
};

const TailwindThemedPortal = (props: {
    readonly children: ReactNode;
    readonly portalElement: HTMLElement;
}): React.ReactPortal | null => {
    const theme = useTheme();
    return ReactDOM.createPortal(
        <TailwindThemeProvider colorTheme={theme.colors}>
            {props.children}
        </TailwindThemeProvider>,

        props.portalElement,
    );
};

export { TailwindThemedPortal, TailwindThemeProvider };
